// Estimate on-camera runtime from a script's AUDIO column only.
//
// Client scripts are two-column (audio | visual). Only the audio column is
// spoken to camera, so only it counts toward runtime — visual directions are
// stage notes for the crew, not narration, and counting them would inflate the
// estimate. ~140 wpm is a calm, considered on-camera delivery pace (slower than
// the ~150 wpm used for the raw whole-doc word counter, because a brand film is
// read deliberately, not rattled off).
//
// Pure + framework-free so it can be unit-tested directly and reused anywhere a
// ProseMirror/Tiptap doc JSON is on hand — including worker-side, where the
// bundle hub's runtime badge (docs/script-bundles-plan.md §5) is computed from
// each ready script's stored doc_json without ever loading the editor.

import { isSpokenAudioType } from './audioTypes';
import { BRIEF_FIELD_NODE, normalizeBriefFieldAttrs } from './briefFields';

/** Speaking pace for the badge and the read-through (2B). 1D moves it into
 *  Settings; until then this constant is the one source. */
export const WORDS_PER_MINUTE = 140;

// Below this, a runtime estimate is noise rather than signal — a near-empty
// script shouldn't claim "runs ≈ 5 seconds on camera".
const MIN_WORDS = 20;

export interface RuntimeEstimate {
  /** Word count of the audio (spoken) column. */
  words: number;
  /** On-camera seconds, rounded to the nearest 5. */
  seconds: number;
}

interface DocNode {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown> | null;
  content?: DocNode[];
}

/** Walk the doc; harvest text from every spoken AUDIO scriptCell, skip
 *  everything else (disabled brief fields, visual cells, headings, stray
 *  prose). Cells don't nest, so once we hit a scriptCell we harvest-or-skip it
 *  and don't descend further. "On-screen text" (audioType 'silent') is not
 *  spoken to camera, so its words don't count toward the runtime. */
function collectAudioText(node: DocNode | null | undefined, out: string[]): void {
  if (!node || typeof node !== 'object') return;
  if (
    node.type === BRIEF_FIELD_NODE &&
    normalizeBriefFieldAttrs(node.attrs).enabled !== true
  ) {
    return;
  }
  if (node.type === 'scriptCell') {
    // Schema default for a cell with no explicit kind is 'audio'.
    const kind = (node.attrs?.kind as string | undefined) ?? 'audio';
    if (kind === 'audio' && isSpokenAudioType(node.attrs?.audioType)) out.push(spokenText(node));
    return;
  }
  if (Array.isArray(node.content)) {
    for (const child of node.content) collectAudioText(child, out);
  }
}

/** Plain text with inline marks joined, and block/hard-break boundaries separated. */
export function spokenText(node: DocNode | null | undefined): string {
  if (!node || typeof node !== 'object') return '';
  if (typeof node.text === 'string') return node.text;
  if (node.type === 'hardBreak') return '\n';
  const text = Array.isArray(node.content) ? node.content.map(spokenText).join('') : '';
  return node.type === 'paragraph' ? text + '\n' : text;
}

export function countWords(text: string): number {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

/**
 * Estimate the on-camera runtime of a script's audio column.
 * Returns null when there are fewer than {@link MIN_WORDS} spoken words (empty
 * scripts, prose-only docs with no audio cells, near-empty drafts).
 */
export function estimateRuntime(doc: unknown): RuntimeEstimate | null {
  const words = countSpokenWords(doc);
  if (words < MIN_WORDS) return null;
  const seconds = Math.round(wordsToSeconds(words) / 5) * 5;
  return { words, seconds };
}

/** Spoken words in the audio column, by the same rule as the runtime badge.
 *  No minimum: a short cutdown still has a word count. */
export function countSpokenWords(doc: unknown): number {
  const parts: string[] = [];
  collectAudioText(doc as DocNode, parts);
  return countWords(parts.join(' '));
}

/** Unrounded on-camera seconds for a word count at the badge's pace. */
export function wordsToSeconds(words: number): number {
  return (words / WORDS_PER_MINUTE) * 60;
}

/**
 * Human duration for the badge. Under 90s reads in seconds
 * ("≈ 80 seconds"); 90s and over reads in minutes ("≈ 1 min 30 sec", or
 * "≈ 2 min" on an exact minute).
 */
export function formatDuration(seconds: number): string {
  if (seconds < 90) return `≈ ${seconds} seconds`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `≈ ${m} min` : `≈ ${m} min ${s} sec`;
}

/** Full badge label, e.g. "~420 words · runs ≈ 80 seconds on camera". */
export function formatRuntimeLabel(est: RuntimeEstimate): string {
  return `~${est.words} words · runs ${formatDuration(est.seconds)} on camera`;
}
